import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Badge, Card, Col, Form, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

import { backgroundGrey1, backgroundGrey4, danger, veryLightGray } from 'lib/css/colors';
import { GLOBAL_PRODUCT_REJECTED_GTINS } from 'lib/networking/endpoints';
import { GLOBAL_PRODUCT_TYPE } from 'lib/enums/ProductType';
import usePost from 'lib/hooks/usePost';

function RejectedGtinsSection({ selectedProductOption, refetch }) {
  const [selectedGtins, setSelectedGtins] = useState([]);
  const productId = selectedProductOption?.productId;
  const productType = selectedProductOption?.productType;
  const rejectedGtins = selectedProductOption?.product?.rejected_gtins || [];

  // Skip for global products since they don't have rejected_gtins
  const shouldShow = productId && productType !== GLOBAL_PRODUCT_TYPE;

  const { postData: manageRejectedGtins, loading: managingGtins } = usePost(
    GLOBAL_PRODUCT_REJECTED_GTINS,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Rejected GTINs updated successfully',
      }).then(() => {
        setSelectedGtins([]);
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to update rejected GTINs: ${
          error?.response?.data?.message || 'Unknown error'
        }`,
      }),
  );

  const handleSelectGtin = gtin => {
    setSelectedGtins(prev =>
      prev.includes(gtin) ? prev.filter(g => g !== gtin) : [...prev, gtin],
    );
  };

  const handleSelectAll = () => {
    if (selectedGtins.length === rejectedGtins.length) {
      setSelectedGtins([]);
    } else {
      setSelectedGtins([...rejectedGtins]);
    }
  };

  const allSelected = selectedGtins.length === rejectedGtins.length && rejectedGtins.length > 0;

  const handleRemoveSelected = () => {
    if (selectedGtins.length === 0) return;

    const gtinText =
      selectedGtins.length === 1
        ? `"${selectedGtins[0]}"`
        : `${selectedGtins.length} selected GTINs`;

    Swal.fire({
      title: 'Remove Selected GTINs',
      text: `Are you sure you want to remove ${gtinText} from the rejected GTINs list?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove them',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        manageRejectedGtins({
          product_id: productId,
          product_type: productType,
          gtins: selectedGtins,
          clear_all: allSelected,
        });
      }
    });
  };

  // Don't render for global products or if no product selected
  if (!shouldShow) {
    return null;
  }

  return (
    <Card className={css(styles.card)}>
      <Card.Header className={css(styles.cardHeader)}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            Rejected GTINs
            {rejectedGtins.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {rejectedGtins.length}
              </Badge>
            )}
          </h6>
          {rejectedGtins.length > 0 && (
            <div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveSelected}
                disabled={managingGtins || selectedGtins.length === 0}
              >
                <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        {rejectedGtins.length === 0 ? (
          <div className={css(styles.emptyState)}>
            <p className="text-muted mb-0">No rejected GTINs found for this product.</p>
          </div>
        ) : (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={allSelected}
                onChange={handleSelectAll}
                className={css(styles.selectAllCheckbox)}
              />
              <p className="text-muted mb-0">
                These GTINs have been rejected and will not be automatically linked to this product:
              </p>
            </div>
            {rejectedGtins.map((gtin, index) => (
              <Row key={index} className={css(styles.gtinRow)}>
                <Col xs={1}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedGtins.includes(gtin)}
                    onChange={() => handleSelectGtin(gtin)}
                  />
                </Col>
                <Col xs={10}>
                  <code className={css(styles.gtinCode)}>{gtin}</code>
                </Col>
              </Row>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

RejectedGtinsSection.propTypes = {
  selectedProductOption: PropTypes.shape({
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productType: PropTypes.string,
    product: PropTypes.shape({
      rejected_gtins: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  refetch: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  card: {
    marginBottom: '1rem',
    border: '1px solid ' + backgroundGrey4,
  },
  cardHeader: {
    backgroundColor: backgroundGrey4,
    fontWeight: 'bold',
  },
  emptyState: {
    textAlign: 'center',
    padding: '1rem',
  },
  gtinRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem',
    padding: '0.5rem',
    border: `1px solid ${veryLightGray}`,
    borderRadius: '4px',
    backgroundColor: backgroundGrey1,
  },
  gtinCode: {
    backgroundColor: danger,
    color: 'white',
    padding: '0.25rem 0.5rem',
    borderRadius: '3px',
    fontSize: '0.875rem',
  },
  selectAllCheckbox: {
    marginRight: '1rem',
  },
});

export default RejectedGtinsSection;
